import { useEffect, useRef } from 'react'
import { Transition } from 'react-transition-group'
import gsap from 'gsap'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Logo from '@/components/base/Logo'
import Burger from '@/components/layout/Burger'
import Menu from '@/components/layout/Menu'
import { useLenis, useLenisStore } from '@/contexts/lenis'
import { useStore } from '@/contexts/store'
import { TRANSITION_LEAVE_DURATION } from '@/contexts/transition'
import { useLayoutStore } from '@/store/layout'
import { useLocaleStore } from '@/store/locale'
import { useRouterStore } from '@/store/router'
import { FRONT_BASE_URL } from '@/constants/index'

import TypographyMask from '../base/TypographyMask'
import { cn } from '@/lib/utils'

function Header() {
  const { menu, activateMaintenance } = useStore()
  const { lenis } = useLenis()
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuOpen, headerHide] = useLayoutStore((state) => [
    state.menuOpen,
    state.headerHide
  ])
  const setLayoutState = useLayoutStore((state) => state.setLayoutState)

  // Legge le traduzioni correnti impostate dal Layout
  const pageTranslations = useLocaleStore((s) => s.currentTranslations)
  const currentLocale = useLocaleStore((s) => s.currentLocale)

  useEffect(() => {
    const unsubscribe = useRouterStore.subscribe(
      (state) => state.currentRoute,
      () =>
        gsap.delayedCall(TRANSITION_LEAVE_DURATION, () =>
          setLayoutState({ menuOpen: false })
        )
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  useEffect(() => {
    if (!lenis) return
    if (menuOpen) {
      lenis.stop()
    } else {
      lenis.start()
    }
  }, [menuOpen, lenis])

  const lastScrollRef = useRef<number | null>(null)

  useEffect(() => {
    const unsubscribe = useLenisStore.subscribe(
      (state) => [state.scroll, state.direction],
      ([scroll, direction]) => {
        const currentScroll = scroll ?? 0
        const activeScroll = currentScroll > 200

        // Aggiorna lo sfondo dell'header in base allo scroll
        if (activeScroll) {
          if (direction === 1) {
            // Scrolling verso il basso: applica un threshold di 20px
            if (lastScrollRef.current === null) {
              // Imposta il punto di riferimento se non è ancora definito
              lastScrollRef.current = currentScroll
            } else if (currentScroll - lastScrollRef.current >= 20) {
              setLayoutState({ headerHide: true })
              // Aggiorna il riferimento per futuri controlli
              lastScrollRef.current = currentScroll
            }
          } else {
            // Scrolling verso l'alto: mostra l'header solo se lo scroll verso l'alto supera i 20px
            if (lastScrollRef.current === null) {
              lastScrollRef.current = currentScroll
            } else if (lastScrollRef.current - currentScroll >= 20) {
              setLayoutState({ headerHide: false })
              lastScrollRef.current = currentScroll
            }
          }
        } else {
          // Se lo scroll non è "attivo", mostra l'header e resetta il riferimento
          setLayoutState({ headerHide: false })
          lastScrollRef.current = null
        }
      }
    )

    return () => {
      unsubscribe()
    }
  }, [setLayoutState])

  return (
    <header
      className={cn([
        'fixed left-0 right-0 top-0 z-header flex h-header-height items-center bg-white',
        'transition-all duration-500 ease-out-cubic',
        {
          '-translate-y-full': headerHide
        }
      ])}
    >
      <Container>
        <Grid className="relative z-20">
          <div
            className={cn([
              activateMaintenance
                ? 'col-span-4 flex items-center justify-center md:col-span-6 lg:col-span-12'
                : 'col-span-2 flex items-center md:col-span-4',
              'transition-transform duration-2200 ease-out-expo',
              {
                'pointer-events-none -translate-y-full': headerHide
              }
            ])}
            data-header-span
          >
            <Link href="/" scroll={false}>
              <Logo />
            </Link>
          </div>
          {!activateMaintenance ? (
            <div
              className={cn([
                'col-span-2 flex items-center justify-end lg:col-span-8',
                'transition-transform duration-2200 ease-out-expo',
                {
                  'pointer-events-none -translate-y-full': headerHide
                }
              ])}
              data-header-span
            >
              <nav className="hidden items-center gap-[45px] lg:flex">
                {menu?.main_menu?.menu?.map(
                  ({ title, url, target, classes }, index) => {
                    if (classes === 'btn') {
                      return (
                        <Button
                          variant="primary"
                          asChild
                          key={`menu-${index}`}
                        >
                          <Link
                            href={url}
                            target={target}
                            className={cn(['whitespace-nowrap'])}
                            data-motion-link-menu
                            scroll={false}
                          >
                            <TypographyMask>
                              <span>{title}</span>
                            </TypographyMask>
                          </Link>
                        </Button>
                      )
                    } else if (classes === 'btn_lang') {
                      let _url = pageTranslations.filter((t) => {
                        return t.code !== currentLocale
                      })

                      return (
                        <Button
                          variant="secondary"
                          asChild
                          key={`menu-${index}`}
                        >
                          <a
                            href={FRONT_BASE_URL + _url[0]?.href || url}
                            target={target}
                            className={cn(['whitespace-nowrap'])}
                            data-motion-link-menu
                          >
                            <TypographyMask>
                              <span>{title}</span>
                            </TypographyMask>
                          </a>
                        </Button>
                      )
                    } else {
                      return (
                        <Link
                          key={`menu-${index}`}
                          href={url}
                          target={target}
                          className={cn([
                            'underline-effect whitespace-nowrap text-center text-body'
                          ])}
                          data-motion-link-menu
                          scroll={false}
                        >
                          {title}
                        </Link>
                      )
                    }
                  }
                )}
              </nav>
              <Burger
                onClick={() => setLayoutState({ menuOpen: !menuOpen })}
                active={menuOpen}
                className="lg:hidden"
              />
            </div>
          ) : null}
        </Grid>

        <Transition
          nodeRef={menuRef}
          in={menuOpen}
          timeout={{
            enter: 0,
            exit: 800
          }}
          unmountOnExit
        >
          {(state) => <Menu ref={menuRef} state={state} menuOpen={menuOpen} />}
        </Transition>
      </Container>
    </header>
  )
}

export default Header
